import { create } from 'zustand';
import type { PaletteObjectId, PlacedObject, WallIndex } from '@/types/object';
import { track } from '@utils/analytics';

/**
 * Max history entries kept on each stack. Cap mirrors the legacy build
 * (20 undos) and protects against unbounded memory growth when the user
 * hammers drag-to-place during exploration.
 */
const MAX_HISTORY = 20;

/* ─── Snapshots / history ──────────────────────────────────────── */

interface Snapshot {
  placedObjects: readonly PlacedObject[];
  selectedObjectUUID: string | null;
}

function cloneSnapshot(s: Snapshot): Snapshot {
  return {
    placedObjects: s.placedObjects.slice(),
    selectedObjectUUID: s.selectedObjectUUID,
  };
}

/** Generate a stable UUID; falls back to a pseudo-random id in older runtimes. */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `p-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

/* ─── Drag / hover transient state (NOT included in history) ─── */

type Vec3 = readonly [number, number, number];

export interface DragGhostInfo {
  position: Vec3;
  rotation: Vec3;
  wallIndex?: WallIndex;
  valid: boolean;
}

interface DragState {
  /** Palette drag in flight — the PaletteObjectId we're dropping. */
  draggingDefId: PaletteObjectId | null;
  /** Existing-object move in flight — the uuid we're dragging. */
  movingUUID: string | null;
  /** Ghost rendering info; null until the first pointer move updates it. */
  ghost: DragGhostInfo | null;
  /** uuid of the placed object under the cursor, or null. */
  hoveredUUID: string | null;
}

/* ─── Store API ────────────────────────────────────────────────── */

export interface AddObjectOptions {
  position?: Vec3;
  rotation?: Vec3;
  wallIndex?: WallIndex;
  /** Skip auto-selecting the new object. */
  skipSelect?: boolean;
}

interface ObjectState extends Snapshot, DragState {
  past: Snapshot[];
  future: Snapshot[];

  /* Placed-object CRUD (pushes history on mutation). */
  addObject: (defId: PaletteObjectId, opts?: AddObjectOptions) => string;
  removeObject: (uuid: string) => void;
  moveObject: (uuid: string, position: Vec3) => void;
  rotateObject: (uuid: string, rotation: Vec3) => void;
  selectObject: (uuid: string | null) => void;
  deselectObject: () => void;
  clearAll: () => void;
  undo: () => void;
  redo: () => void;

  canUndo: () => boolean;
  canRedo: () => boolean;

  /* Drag lifecycle (transient — does NOT push history until complete). */
  beginDragFromPalette: (defId: PaletteObjectId) => void;
  beginMoveExisting: (uuid: string) => void;
  updateGhost: (ghost: DragGhostInfo) => void;
  completeDrag: () => void;
  cancelDrag: () => void;

  /* Hover (transient only — used for cyan outline on mouse-over). */
  setHovered: (uuid: string | null) => void;
}

/* Internal: append current state to `past` and clear `future` before mutation. */
function commitHistory(state: ObjectState): Pick<ObjectState, 'past' | 'future'> {
  const past = [
    ...state.past,
    cloneSnapshot({
      placedObjects: state.placedObjects,
      selectedObjectUUID: state.selectedObjectUUID,
    }),
  ].slice(-MAX_HISTORY);
  return { past, future: [] };
}

export const useObjectStore = create<ObjectState>((set, get) => ({
  /* ─── Snapshot + history ───── */
  placedObjects: [],
  selectedObjectUUID: null,
  past: [],
  future: [],

  /* ─── Transient UI state ───── */
  draggingDefId: null,
  movingUUID: null,
  ghost: null,
  hoveredUUID: null,

  /* ─── Placed-object CRUD ───── */

  addObject: (defId, opts = {}) => {
    const uuid = generateUUID();
    const {
      position = [0, 0, 0],
      rotation = [0, 0, 0],
      wallIndex,
      skipSelect = false,
    } = opts;
    const placed: PlacedObject = {
      uuid,
      defId,
      position,
      rotation,
      ...(wallIndex !== undefined ? { wallIndex } : {}),
    };
    set((state) => ({
      ...commitHistory(state),
      placedObjects: [...state.placedObjects, placed],
      selectedObjectUUID: skipSelect ? state.selectedObjectUUID : uuid,
    }));
    track('object_placed', { defId });
    return uuid;
  },

  removeObject: (uuid) => {
    const removed = get().placedObjects.find((o) => o.uuid === uuid);
    set((state) => {
      const filtered = state.placedObjects.filter((o) => o.uuid !== uuid);
      if (filtered.length === state.placedObjects.length) return state;
      return {
        ...commitHistory(state),
        placedObjects: filtered,
        selectedObjectUUID:
          state.selectedObjectUUID === uuid ? null : state.selectedObjectUUID,
      };
    });
    if (removed) track('object_removed', { defId: removed.defId });
  },

  moveObject: (uuid, position) =>
    set((state) => {
      const idx = state.placedObjects.findIndex((o) => o.uuid === uuid);
      if (idx === -1) return state;
      const next = state.placedObjects.slice();
      const existing = next[idx]!;
      next[idx] = { ...existing, position };
      return {
        ...commitHistory(state),
        placedObjects: next,
      };
    }),

  rotateObject: (uuid, rotation) =>
    set((state) => {
      const idx = state.placedObjects.findIndex((o) => o.uuid === uuid);
      if (idx === -1) return state;
      const next = state.placedObjects.slice();
      const existing = next[idx]!;
      next[idx] = { ...existing, rotation };
      return {
        ...commitHistory(state),
        placedObjects: next,
      };
    }),

  selectObject: (uuid) => set({ selectedObjectUUID: uuid }),
  deselectObject: () => set({ selectedObjectUUID: null }),

  clearAll: () =>
    set((state) => {
      if (state.placedObjects.length === 0) return state;
      return {
        ...commitHistory(state),
        placedObjects: [],
        selectedObjectUUID: null,
      };
    }),

  undo: () =>
    set((state) => {
      const prev = state.past[state.past.length - 1];
      if (!prev) return state;
      return {
        past: state.past.slice(0, -1),
        future: [
          cloneSnapshot({
            placedObjects: state.placedObjects,
            selectedObjectUUID: state.selectedObjectUUID,
          }),
          ...state.future,
        ].slice(0, MAX_HISTORY),
        placedObjects: prev.placedObjects,
        selectedObjectUUID: prev.selectedObjectUUID,
      };
    }),

  redo: () =>
    set((state) => {
      const nextSnap = state.future[0];
      if (!nextSnap) return state;
      return {
        past: [
          ...state.past,
          cloneSnapshot({
            placedObjects: state.placedObjects,
            selectedObjectUUID: state.selectedObjectUUID,
          }),
        ].slice(-MAX_HISTORY),
        future: state.future.slice(1),
        placedObjects: nextSnap.placedObjects,
        selectedObjectUUID: nextSnap.selectedObjectUUID,
      };
    }),

  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,

  /* ─── Drag lifecycle ───── */

  beginDragFromPalette: (defId) =>
    set({ draggingDefId: defId, movingUUID: null, ghost: null }),

  beginMoveExisting: (uuid) =>
    set({ movingUUID: uuid, draggingDefId: null, ghost: null }),

  updateGhost: (ghost) => set({ ghost }),

  completeDrag: () => {
    const state = get();
    const { draggingDefId, movingUUID, ghost } = state;
    if (ghost && ghost.valid) {
      if (draggingDefId) {
        get().addObject(draggingDefId, {
          position: ghost.position,
          rotation: ghost.rotation,
          wallIndex: ghost.wallIndex,
        });
      } else if (movingUUID) {
        get().moveObject(movingUUID, ghost.position);
      }
    }
    set({ draggingDefId: null, movingUUID: null, ghost: null });
  },

  cancelDrag: () =>
    set({ draggingDefId: null, movingUUID: null, ghost: null }),

  /* ─── Hover ───── */

  setHovered: (uuid) => set({ hoveredUUID: uuid }),
}));
